import React, { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, Button, Select, MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RoomIcon from '@material-ui/icons/Room';
import FlagIcon from '@material-ui/icons/Flag';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import DirectionsIcon from '@material-ui/icons/Directions';
import TimerIcon from '@material-ui/icons/Timer';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import HotelIcon from '@material-ui/icons/Hotel';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PersonIcon from '@material-ui/icons/Person';
import { t } from '../../i18n';

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialog: {
    background: '#ffffff',
    borderRadius: 16,
    width: 440,
    maxWidth: '92vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    background: 'linear-gradient(180deg, #ffffff 0%, #dbeafe 100%)',
    borderBottom: '1px solid rgba(0,0,0,0.07)',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  body: {
    padding: '20px',
  },
  section: {
    marginBottom: 16,
  },
  sectionLabel: {
    fontSize: '0.62rem',
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 8,
  },
  nameInput: {
    width: '100%',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: '0.88rem',
    fontWeight: 600,
    color: '#1e293b',
    outline: 'none',
    transition: 'all 0.2s',
    background: '#f8fafc',
    '&:focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59,130,246,0.08)',
      background: '#fff',
    },
  },
  infoCard: {
    background: '#f8fafc',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    padding: '12px 14px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
    '&:last-child': { marginBottom: 0 },
  },
  infoLabel: {
    fontSize: '0.72rem',
    color: '#64748b',
    minWidth: 70,
  },
  infoValue: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#1e293b',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  statCard: {
    background: '#f8fafc',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  statIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  statValue: {
    fontSize: '0.88rem',
    fontWeight: 700,
    color: '#1e293b',
    lineHeight: 1.1,
  },
  statLabel: {
    fontSize: '0.6rem',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  assignmentRow: {
    display: 'flex',
    gap: 10,
  },
  assignmentCard: {
    flex: 1,
    background: '#f8fafc',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '14px 20px',
    borderTop: '1px solid #e2e8f0',
    background: '#f8fafc',
  },
  cancelBtn: {
    fontSize: '0.78rem',
    textTransform: 'none',
    color: '#64748b',
    borderColor: '#e2e8f0',
    borderRadius: 8,
    padding: '6px 16px',
  },
  saveBtn: {
    fontSize: '0.78rem',
    textTransform: 'none',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#fff',
    borderRadius: 8,
    padding: '6px 20px',
    boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
    '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' },
    '&:disabled': { background: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
  },
  successOverlay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    gap: 12,
  },
  updateBadge: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 6,
    fontSize: '0.6rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginLeft: 8,
  },
}));

function SaveRouteDialog({ open, onClose, route, vehicles, drivers, lang, onSave, saving }) {
  const classes = useStyles();
  const [routeName, setRouteName] = useState(route.name || '');
  const [saved, setSaved] = useState(false);
  // Track which button was clicked so we can show the right loading label.
  const [savingMode, setSavingMode] = useState(null); // 'save' | 'dispatch' | null

  // Sync name and existing assignments when dialog opens
  useEffect(() => {
    if (open) {
      setRouteName(route.name || '');
      setSaved(false);
    }
  }, [open, route.name]);

  // Show success when saving transitions from true to false
  const prevSavingRef = React.useRef(false);
  useEffect(() => {
    if (open && prevSavingRef.current && !saving) {
      setSaved(true);
    }
    prevSavingRef.current = saving;
  }, [saving, open]);

  if (!open) return null;

  const distanceKm = route.computedRoute
    ? (route.computedRoute.distance / 1000).toFixed(1)
    : null;
  const durationH = route.computedRoute ? Math.floor(route.computedRoute.duration / 3600) : null;
  const durationM = route.computedRoute ? Math.floor((route.computedRoute.duration % 3600) / 60) : null;

  const selectedVehicle = vehicles?.find(v => String(v.id) === String(route.vehicleId));
  const selectedDriver = drivers?.find(d => String(d.id) === String(route.driverId));
  const isUpdate = !!route.dbId;

  // Para despachar precisamos de saber A QUEM enviar a rota. Sem
  // motorista atribuído (e idealmente também com um veículo), o
  // "Despachar" não tem sentido — esconde-se o botão e mostra-se
  // uma dica para o operador.
  const canDispatch = !!(route.driverId && route.vehicleId);

  // dispatch=true  → save AND notify the driver (legacy "Save & Dispatch")
  // dispatch=false → save only; driver is NOT notified. Use case: operator
  //                  is preparing future routes and doesn't want to ping
  //                  the driver until the day arrives.
  function handleSave(dispatchToDriver) {
    setSavingMode(dispatchToDriver ? 'dispatch' : 'save');
    onSave(routeName, dispatchToDriver);
  }

  if (saved) {
    return (
      <Box className={classes.overlay} onClick={onClose}>
        <Box className={classes.dialog} onClick={e => e.stopPropagation()}>
          <Box className={classes.successOverlay}>
            <CheckCircleIcon style={{ fontSize: 56, color: '#3b82f6' }} />
            <Typography style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
              {isUpdate ? t(lang, 'routeUpdated') : t(lang, 'routeSaved')}
            </Typography>
            <Typography style={{ fontSize: '0.8rem', color: '#64748b', textAlign: 'center' }}>
              {routeName || t(lang, 'routeNoName')}
            </Typography>
            <Button
              variant="outlined"
              className={classes.cancelBtn}
              onClick={onClose}
              style={{ marginTop: 12 }}
            >
              {t(lang, 'close')}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.overlay} onClick={onClose}>
      <Box className={classes.dialog} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <Box className={classes.header}>
          <Box className={classes.headerTitle}>
            <SaveIcon style={{ color: '#3b82f6', fontSize: 20 }} />
            <Box>
              <Typography style={{ fontSize: '0.9rem', fontWeight: 600, color: '#0f172a' }}>
                {t(lang, 'headerSaveRoute')}
                {isUpdate && (
                  <span className={classes.updateBadge} style={{ background: 'rgba(245,158,11,0.2)', color: '#f59e0b' }}>
                    {t(lang, 'updateLabel')}
                  </span>
                )}
              </Typography>
              <Typography style={{ fontSize: '0.65rem', color: '#64748b' }}>
                {isUpdate ? t(lang, 'saveRouteUpdateDesc') : t(lang, 'saveRouteNewDesc')}
              </Typography>
            </Box>
          </Box>
          <Button size="small" onClick={onClose} startIcon={<CloseIcon style={{ fontSize: 14 }} />}
            style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'none', fontWeight: 600, borderRadius: 8, border: '1px solid #e2e8f0', padding: '4px 12px', background: '#fff' }}>
            Fechar
          </Button>
        </Box>

        {/* Body */}
        <Box className={classes.body}>
          {/* Route Name */}
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>{t(lang, 'routeName')}</Typography>
            <input
              className={classes.nameInput}
              value={routeName}
              onChange={e => setRouteName(e.target.value)}
              placeholder={t(lang, 'routeNoName')}
              autoFocus
            />
          </Box>

          {/* Origin / Destination */}
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>{t(lang, 'journey')}</Typography>
            <Box className={classes.infoCard}>
              <Box className={classes.infoRow}>
                <RoomIcon style={{ fontSize: 15, color: '#22c55e' }} />
                <Typography className={classes.infoValue} style={{ fontSize: '0.76rem' }}>
                  {route.origin?.name || '-'}
                </Typography>
              </Box>
              {route.waypoints && route.waypoints.length > 0 && (
                <Box className={classes.infoRow} style={{ paddingLeft: 4 }}>
                  <Typography style={{ fontSize: '0.68rem', color: '#94a3b8', fontStyle: 'italic' }}>
                    {route.waypoints.length} {route.waypoints.length === 1 ? t(lang, 'waypoint') : t(lang, 'waypoint') + 's'}
                  </Typography>
                </Box>
              )}
              <Box className={classes.infoRow}>
                <FlagIcon style={{ fontSize: 15, color: '#ef4444' }} />
                <Typography className={classes.infoValue} style={{ fontSize: '0.76rem' }}>
                  {route.destination?.name || '-'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Stats Grid */}
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>{t(lang, 'routeDetails')}</Typography>
            <Box className={classes.statsGrid}>
              <Box className={classes.statCard}>
                <Box className={classes.statIcon} style={{ background: 'rgba(59,130,246,0.08)' }}>
                  <DirectionsIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                </Box>
                <Box>
                  <Typography className={classes.statValue}>{distanceKm || '-'} km</Typography>
                  <Typography className={classes.statLabel}>{t(lang, 'distance')}</Typography>
                </Box>
              </Box>
              <Box className={classes.statCard}>
                <Box className={classes.statIcon} style={{ background: 'rgba(245,158,11,0.08)' }}>
                  <TimerIcon style={{ fontSize: 16, color: '#f59e0b' }} />
                </Box>
                <Box>
                  <Typography className={classes.statValue}>
                    {durationH != null ? `${durationH}h ${durationM}min` : '-'}
                  </Typography>
                  <Typography className={classes.statLabel}>{t(lang, 'duration')}</Typography>
                </Box>
              </Box>
              <Box className={classes.statCard}>
                <Box className={classes.statIcon} style={{ background: 'rgba(139,92,246,0.08)' }}>
                  <HotelIcon style={{ fontSize: 16, color: '#8b5cf6' }} />
                </Box>
                <Box>
                  <Typography className={classes.statValue}>{route.stops?.length || 0}</Typography>
                  <Typography className={classes.statLabel}>{t(lang, 'stops')}</Typography>
                </Box>
              </Box>
              <Box className={classes.statCard}>
                <Box className={classes.statIcon} style={{ background: 'rgba(239,68,68,0.08)' }}>
                  <LocalGasStationIcon style={{ fontSize: 16, color: '#ef4444' }} />
                </Box>
                <Box>
                  <Typography className={classes.statValue}>{t(lang, route.fuelType === 'diesel' ? 'fuelDieselShort' : route.fuelType === 'gasoline' ? 'fuelGasolineShort' : route.fuelType === 'gnl' ? 'fuelGNL' : 'fuelElectric')}</Typography>
                  <Typography className={classes.statLabel}>{t(lang, 'fuel')}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Vehicle / Driver Assignment */}
          {(selectedVehicle || selectedDriver) && (
            <Box className={classes.section}>
              <Typography className={classes.sectionLabel}>{t(lang, 'assignment')}</Typography>
              <Box className={classes.assignmentRow}>
                {selectedVehicle && (
                  <Box className={classes.assignmentCard}>
                    <LocalShippingIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                    <Box>
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>
                        {selectedVehicle.plate}
                      </Typography>
                      <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                        {selectedVehicle.type || ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {selectedDriver && (
                  <Box className={classes.assignmentCard}>
                    <PersonIcon style={{ fontSize: 16, color: '#8b5cf6' }} />
                    <Box>
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>
                        {selectedDriver.name}
                      </Typography>
                      <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                        {selectedDriver.licenseClass || ''}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Driver/Vehicle assignment removed — now managed via Dispatch */}

          {/* Cargo Ref */}
          {route.cargoRef && (
            <Box className={classes.section} style={{ marginBottom: 0 }}>
              <Box className={classes.infoCard} style={{ padding: '8px 14px' }}>
                <Box className={classes.infoRow}>
                  <Typography className={classes.infoLabel}>{t(lang, 'cargoRef')}:</Typography>
                  <Typography className={classes.infoValue}>{route.cargoRef}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer — 2 ou 3 botões consoante o estado:
            • Cancelar
            • Só gravar  (route stored as planned, driver NOT notified)
            • Gravar e despachar  (só quando há veículo + motorista)
            Sem veículo/motorista atribuídos, "Despachar" não tem
            destinatário — escondemo-lo e mostramos uma dica em vez
            disso, para o operador saber porque o botão sumiu. */}
        <Box className={classes.footer} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          {!canDispatch && (
            <Box style={{
              fontSize: '0.66rem', color: '#92400e',
              background: '#fef3c7', border: '1px solid #fde047',
              padding: '6px 10px', borderRadius: 6,
              lineHeight: 1.4,
            }}>
              💡 {lang === 'pt'
                ? 'Para despachar a rota directamente ao motorista, atribua um veículo e um motorista antes de gravar. Por agora, apenas é possível "Só gravar" (planeada).'
                : 'To dispatch the route directly to the driver, assign a vehicle and a driver before saving. For now only "Save only" (planned) is available.'}
            </Box>
          )}
          <Box style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button variant="outlined" className={classes.cancelBtn} onClick={onClose} disabled={saving}>
              {t(lang, 'cancel')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleSave(false)}
              disabled={saving}
              startIcon={
                saving && savingMode === 'save'
                  ? <HourglassEmptyIcon style={{ fontSize: 16, animation: 'spinHg 1.4s ease-in-out infinite' }} />
                  : <SaveIcon style={{ fontSize: 16 }} />
              }
              style={{
                fontSize: '0.78rem', textTransform: 'none',
                borderColor: '#94a3b8', color: '#1e293b',
                borderRadius: 8, padding: '6px 16px', fontWeight: 600,
              }}
            >
              {saving && savingMode === 'save'
                ? (lang === 'pt' ? 'A guardar...' : 'Saving...')
                : (lang === 'pt' ? 'Só gravar' : 'Save only')}
            </Button>
            {canDispatch && (
              <Button
                className={classes.saveBtn}
                onClick={() => handleSave(true)}
                disabled={saving}
                startIcon={
                  saving && savingMode === 'dispatch'
                    ? <HourglassEmptyIcon style={{ fontSize: 16, animation: 'spinHg 1.4s ease-in-out infinite' }} />
                    : <SaveIcon style={{ fontSize: 16 }} />
                }
              >
                {saving && savingMode === 'dispatch'
                  ? (lang === 'pt' ? 'A despachar...' : 'Dispatching...')
                  : (lang === 'pt' ? 'Gravar e despachar' : 'Save & dispatch')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SaveRouteDialog;
